import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Outlet } from "react-router-dom";
import { useEffect } from "react";
import { ThemeProvider } from "@/components/ThemeProvider";
import { ScrollToTop } from "@/components/ScrollToTop";
import { Suspense } from "react";
import RouteLoadingFallback from "./components/RouteLoadingFallback";
import CookieConsentBanner from "./components/CookieConsentBanner";
import { initPostHog } from "@/lib/posthog";
import { captureFirstTouch } from "@/lib/attribution";

const AppLayout = () => {
  // Initialize PostHog client-side once (opted out until cookie consent).
  // Deferred to the first interaction or 4 s after load, whichever first —
  // the same pattern index.html uses for GA4. The posthog chunk (69 KB) plus
  // its config + surveys.js (35 KB) were landing at 1.0–2.2 s, before the LCP
  // element rendered, so Lighthouse/PSI charged them to LCP (2026-09-01 lab
  // waterfalls). Nothing renders from PostHog (no feature flags), and the
  // cookie banner writes localStorage before phOptIn(), so a deferred init
  // still picks up consent via its `loaded` callback.
  useEffect(() => {
    captureFirstTouch();
    if (typeof window === "undefined") return;
    const events = ["pointerdown", "keydown", "touchstart", "scroll"] as const;
    let timer: number | undefined;
    const cleanup = () => {
      events.forEach((e) => window.removeEventListener(e, fire, true));
      if (timer !== undefined) window.clearTimeout(timer);
      window.removeEventListener("load", arm);
    };
    const fire = () => {
      cleanup();
      initPostHog();
    };
    const arm = () => {
      timer = window.setTimeout(fire, 4000);
    };
    events.forEach((e) => window.addEventListener(e, fire, { capture: true, passive: true }));
    if (document.readyState === "complete") arm();
    else window.addEventListener("load", arm, { once: true });
    return cleanup;
  }, []);

  return (
    <ThemeProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <ScrollToTop />
          <Suspense fallback={<RouteLoadingFallback />}>
            <Outlet />
          </Suspense>
          <CookieConsentBanner />
        </TooltipProvider>
    </ThemeProvider>
  );
};

export default AppLayout;
